import { supabase } from './supabase'
import type { Session } from '@/types'

/**
 * Ensures that a profile row exists for the current user.
 * If not, it creates one using the session metadata.
 * @param session - The Supabase session object
 * @returns True if profile exists or was created, False on error
 */
export async function ensureProfileExists(session: Session | null): Promise<boolean> {
    if (!session?.user) return false

    try {
        // 1. Check if profile exists
        const { data, error } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', session.user.id)
            .maybeSingle()

        if (error && error.code !== 'PGRST116') {
            console.error('Error checking profile:', error)
            return false
        }

        if (data) return true // Profile exists

        // 2. Create profile if missing
        // We use data from auth metadata
        const { user } = session
        const updates = {
            id: user.id,
            email: user.email,
            full_name: user.user_metadata?.full_name || '',
            avatar_url: user.user_metadata?.avatar_url || '',
            updated_at: new Date().toISOString(),
        }

        const { error: insertError } = await supabase
            .from('profiles')
            .insert(updates)

        if (insertError) {
            console.error('Error creating profile:', insertError)
            return false
        }

        return true
    } catch (err) {
        console.error('Unexpected error in ensureProfileExists:', err)
        return false
    }
}
